import { useEffect, useMemo, useState } from 'react';
import FeaturedSoftwareCoursesHeader from './subcomponents/FeaturedSoftwareCoursesHeader';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import UsageTracker from '../../utils/UsageTracker';
import { getRecommendedSoftwareByField, getRecommendedSoftwareByLanguage, getGenericRecommendedSoftware, SoftwareRecommendationsViewMoreListMap } from '../../features/recommendations';
import getLang from 'LearningCenterUI/utils/getLang';
import { usePreviouslySelectedTabStore } from '../../features/recommendations/recommendationsStore';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { FeaturedSoftwareCoursesTabs, useSoftwareRecommendationsTabs } from './subcomponents/FeaturedSoftwareCoursesTabs';
import LearningContentCard from '../../features/cards/components/LearningContentCard';
import { getSearchUrl } from '../../features/search/searchRouting';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import I18n from 'I18n';
import HomePageSection from '../Layouts/HomePageSection';
import { Flex } from 'foundations-components/components/Flex';
import { RouterLink } from '../AcademyLinks';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const useSoftwareRecommendationsContent = (selectedTab, maxResults = 6) => {
  const language = getLang();
  const contentToUse = useMemo(() => {
    let contentList;
    if (language === 'en') {
      contentList = getRecommendedSoftwareByField(selectedTab);
    } else {
      contentList = getRecommendedSoftwareByLanguage(language);
    }
    if (!contentList) contentList = getGenericRecommendedSoftware();
    return contentList.slice(0, maxResults);
  }, [language, maxResults, selectedTab]);
  const {
    cardModels,
    cardModelsLoading: isLoadingContent
  } = useLearningContentCardModels({
    items: contentToUse.map(({
      id,
      type
    }) => ({
      id,
      type,
      language: getLang()
    })),
    skip: !contentToUse.length
  });
  const tool = SoftwareRecommendationsViewMoreListMap[selectedTab];
  const viewAllLink = tool ? getSearchUrl({
    tool
  }) : null;
  return {
    cardModels,
    isLoadingContent,
    viewAllLink
  };
};
const FeaturedSoftwareCoursesSection = () => {
  const {
    isMobile
  } = useMediaQuery();
  const {
    isLoading: isLoadingTabs,
    defaultSelectedTab
  } = useSoftwareRecommendationsTabs();
  const maxResults = isMobile ? 5 : 6;
  const [selectedTab, setSelectedTab] = useState('general-overview');
  const {
    cardModels,
    isLoadingContent,
    viewAllLink
  } = useSoftwareRecommendationsContent(selectedTab, maxResults);
  const {
    setPreviouslySelectedTab
  } = usePreviouslySelectedTabStore();
  const isLoading = isLoadingTabs || isLoadingContent;
  const handleSelectionChange = tab => {
    UsageTracker.track('interaction', {
      action: 'featured-software-switched-tab',
      tab
    });
    setSelectedTab(tab);
    setPreviouslySelectedTab(tab);
  };
  useEffect(() => {
    setSelectedTab(defaultSelectedTab);
  }, [defaultSelectedTab]);
  const showTabs = !isLoading && getLang() === 'en';
  const viewAllRouterLink = viewAllLink && (cardModels === null || cardModels === void 0 ? void 0 : cardModels.length) && cardModels.length < maxResults && /*#__PURE__*/_jsx(RouterLink, {
    scrollToTop: true,
    to: viewAllLink,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyComponents.reducedContentCards.allHubCourses",
      options: {
        hub: I18n.text(`learningCenter.learningTopics.options.${selectedTab}`)
      }
    })
  });
  const content = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FeaturedSoftwareCoursesHeader, {
      isLoadingData: isLoading,
      selectedTab: selectedTab,
      viewAllLink: getLang() !== 'en' ? viewAllRouterLink : undefined
    }), showTabs && /*#__PURE__*/_jsx(FeaturedSoftwareCoursesTabs, {
      onSelectedChange: handleSelectionChange,
      selectedTab: selectedTab
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: isMobile ? 'column-reverse' : 'column',
      flexWrap: "wrap",
      gap: '300',
      marginBlockEnd: isMobile ? '300' : '0',
      children: [showTabs && viewAllRouterLink && /*#__PURE__*/_jsx(Flex, {
        inlineSize: "100%",
        justifyContent: isMobile ? 'start' : 'end',
        children: viewAllRouterLink
      }), /*#__PURE__*/_jsx(View, {
        inlineSize: "100%",
        children: /*#__PURE__*/_jsx(CardGrid, {
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, max(350px, 100%/4)), 1fr))",
          gutterSize: isMobile ? 12 : 24,
          marginBlockStart: !viewAllRouterLink && !isMobile ? '900' : '0',
          children: cardModels === null || cardModels === void 0 ? void 0 : cardModels.map((item, index) => /*#__PURE__*/_jsx(LearningContentCard, {
            item: item,
            onItemClick: () => {
              UsageTracker.trackStandalone('Learning Center Interaction', {
                action: 'featured-software-content-card-click',
                contentId: item.id,
                contentType: item.type
              });
            },
            overviewType: "featured-software",
            position: index + 1,
            size: "small",
            use: "on-surface-recessed"
          }, item.id))
        })
      })]
    })]
  });
  return /*#__PURE__*/_jsx(HomePageSection, {
    paddingBlockStart: "0",
    children: content
  });
};
export default FeaturedSoftwareCoursesSection;